import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import * as Yup from "yup";

import { Button, Center, Checkbox, Text, useToast } from "@chakra-ui/react";
import { SmartForm } from "@equidefi/portals/components/smart-form";
import { Heading } from "@equidefi/ui";

import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { usePasswordStrength } from "@equidefi/portals/hooks/usePasswordStrength";
import { useSetNewPassword, useVerifyPasswordToken } from "@equidefi/portals/hooks/useUsers";

import LoginContainer from "./LoginContainer";

export const buildForgotPasswordSchema = (validateFn: (password: string) => true | string) =>
  Yup.object().shape({
    password: Yup.string()
      .required("Password is required")
      .test(function (password = "") {
        const result = validateFn(password);

        return result === true
          ? true
          : this.createError({
              message: result,
              path: "password",
            });
      }),
    confirmPassword: Yup.string()
      .required("Please enter the password again")
      .oneOf([Yup.ref("password"), null], "Passwords must match"),
  });

type TFormValues = {
  password: string;
  confirmPassword: string;
};

const ResetPassword = () => {
  const params = useParams();
  const { redirectUrl = "/" } = useSearchParams() as unknown as { redirectUrl?: string };
  const navigate = useNavigate();
  const toast = useToast({ status: "info" });
  useDocumentTitle("Reset Password");
  const setNewPassword = useSetNewPassword();
  const [showPassword, setShowPassword] = useState(false);
  const { score, analyze, validate } = usePasswordStrength();
  const verifyPasswordToken = useVerifyPasswordToken({
    onInvalid: () => {
      toast({
        status: "error",
        description: "Sorry, the reset password URL is no longer valid",
      });
      navigate("/forgot", { replace: true });
    },
  });

  const schema = useMemo(() => buildForgotPasswordSchema(validate), [validate]);
  const form = useForm<TFormValues>({
    defaultValues: { password: "", confirmPassword: "" },
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  // Score only on edits, so the strength meter stays hidden until the first keystroke.
  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (name === "password") analyze(values.password ?? "");
    });
    return () => subscription.unsubscribe();
  }, [form, analyze]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: verifyPasswordToken is a new function every render; verify once per token
  useEffect(() => {
    verifyPasswordToken({
      token: params.token,
      userId: params.userId,
    });
  }, [params.token, params.userId]);

  const onSubmit = async ({ password }: TFormValues) => {
    try {
      await setNewPassword.mutateAsync({
        password,
        token: params.token,
        userId: params.userId,
      });

      toast({
        description: "Your password has been updated, please login to continue",
      });
      navigate(redirectUrl);
    } catch (error) {
      const errorMessage = error.response?.data?.error;
      toast({
        status: "error",
        description: errorMessage || "An error occured while trying to process your request.",
      });
    }
  };

  return (
    <LoginContainer>
      <SmartForm form={form} onSubmit={onSubmit} noValidate w="full">
        {({ Password }) => (
          <>
            <Heading color="white" fontWeight="bold" m="0" textStyle="h3">
              Reset Password
            </Heading>
            <Text color="white" mb="5" textStyle="body2">
              Enter a secure password for your account
            </Text>
            <Password
              name="password"
              placeholder="Password"
              h="12"
              mb="5"
              showPasswordOverwrite={showPassword}
              strength={score}
            />

            <Password
              name="confirmPassword"
              placeholder="Confirm Password"
              h="12"
              showPasswordOverwrite={showPassword}
            />

            <Checkbox mb="6" color="white" checked={showPassword} onChange={() => setShowPassword((prev) => !prev)}>
              Show password
            </Checkbox>

            <Button
              isLoading={setNewPassword.isLoading}
              isDisabled={!form.formState.isValid}
              type="submit"
              h="12"
              w="full">
              Reset Password
            </Button>

            <Center mt="5">
              <Text fontSize="sm" color="gray.400" mb="0" as={Link} _hover={{ color: "gray.500" }} to={redirectUrl}>
                Remember your password? Log in
              </Text>
            </Center>
          </>
        )}
      </SmartForm>
    </LoginContainer>
  );
};

export default ResetPassword;
